import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  FaMicrophone, 
  FaHeadphones, 
  FaBookOpen, 
  FaCommentDots, 
  FaLock, 
  FaUnlock, 
  FaCheckCircle, 
  FaClock, 
  FaFileAlt, 
  FaQuestionCircle, 
  FaChevronDown, 
  FaChevronUp,
  FaPlay,
  FaVolumeUp,
  FaPuzzlePiece
} from 'react-icons/fa';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';

const speakingSections = [
    { id: 'readAloud', name: 'Read Aloud (Passage Reading)', icon: <FaBookOpen />, count: '8 Tasks', description: 'Read sentences aloud smoothly with natural cadence and correct word stress.' },
    { id: 'repeats', name: 'Repeat Sentences', icon: <FaMicrophone />, count: '16 Tasks', description: 'Listen to spoken sentences once and repeat them with 100% phonetic accuracy.' },
    { id: 'sentenceBuilds', name: 'Sentence Builds', icon: <FaPuzzlePiece />, count: '10 Tasks', description: 'Listen to 3 scrambled phrases and combine them into a correct sentence.' },
    { id: 'conversations', name: 'Conversations (Part D)', icon: <FaCommentDots />, count: '12 Tasks', description: 'Listen to a short workplace dialogue and answer questions directly.' },
    { id: 'shortAnswer', name: 'Short Answer Questions', icon: <FaVolumeUp />, count: '10 Tasks', description: 'Answer general knowledge audio prompts using a single word or short phrase.' },
    { id: 'storyRetelling', name: 'Story Retelling', icon: <FaHeadphones />, count: '3 Tasks', description: 'Listen to a 30-second brief story and retell all details in your own words.' },
    { id: 'openQuestions', name: 'Open Questions', icon: <FaCommentDots />, count: '2 Tasks', description: 'Speak for 40 seconds on a professional topic giving structured opinions.' }
];

const SpeakingHub = () => {
    const navigate = useNavigate();
    const [isPremium, setIsPremium] = useState(false);
    const [openFaq, setOpenFaq] = useState(null);

    // 20 Full Length Speaking Mock Tests
    const tests = Array.from({ length: 20 }, (_, i) => ({
        id: i + 1,
        title: `Versant Speaking & Listening Mock ${i + 1}`,
        desc: 'Full 63-question assessment covering Read Aloud, Repeats, Sentence Builds, Conversations & Story Retelling.',
        duration: '18 mins',
        questions: 63,
        difficulty: i < 5 ? 'Easy' : i < 15 ? 'Medium' : 'Hard'
    }));

    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            if (user) {
                const docRef = doc(db, 'users', user.uid);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists() && (docSnap.data().paidTests || docSnap.data().hasPaid)) {
                    setIsPremium(true);
                } else {
                    setIsPremium(false);
                }
            } else {
                setIsPremium(false);
            }
        });
        
        return () => unsubscribe();
    }, []);

    const handleStartTest = (testId) => {
        if (!isPremium && testId > 1) {
            navigate('/pricing');
            return;
        }
        navigate('/versant-speaking-and-listening-practice-test/start/full', { state: { testId } });
    };

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const faqs = [
        {
            q: "How long is the Versant Speaking and Listening Test?",
            a: "The standard Versant Speaking and Listening test takes approximately 18 to 20 minutes to complete. It consists of 63 questions divided across 7 sub-sections."
        },
        {
            q: "How does the AI grade speech pronunciation and fluency?",
            a: "Our speech recognition AI relies on acoustic models trained on native and non-native English audio samples. It analyzes your speech rate, pause duration, stress patterns, and vowel clarity, generating a 20-80 GSE score aligned with CEFR benchmarks."
        },
        {
            q: "What is a good Versant Speaking score for BPO and IT jobs?",
            a: "Most global BPOs, IT companies, and customer success teams (e.g., Amazon, Teleperformance, Accenture, Concentrix) require an overall score of 58-65+ out of 80 (B2 level on the CEFR scale)."
        },
        {
            q: "Can I practice individual sections like Repeat Sentences or Story Retelling?",
            a: "Yes! Below the 20 full mock tests, you will find section-by-section practice modules to isolate and train specific skill gaps like Repeat Sentences or Story Retelling."
        }
    ];

    return (
        <div className="bg-slate-50 min-h-screen text-slate-900 font-sans selection:bg-emerald-200">
            <Helmet>
                <title>Versant Speaking and Listening Test Practice 2026 | Free AI Mock Exams</title>
                <meta name="description" content="Practice the Versant Speaking and Listening test with 20 free mock exams covering Read Aloud, Repeat Sentences, Sentence Builds, and Story Retelling. Instant AI grading." />
                <link rel="canonical" href="https://versantpro.com/versant-speaking-and-listening-practice-test" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@graph": [
                            {
                                "@type": "WebPage",
                                "@id": "https://versantpro.com/versant-speaking-and-listening-practice-test/#webpage",
                                "url": "https://versantpro.com/versant-speaking-and-listening-practice-test",
                                "name": "Versant Speaking and Listening Test Practice 2026",
                                "description": "Practice the Versant Speaking and Listening test with 20 free mock exams covering Read Aloud, Repeat Sentences, Sentence Builds, and Story Retelling."
                            },
                            {
                                "@type": "EducationalApplication",
                                "@id": "https://versantpro.com/versant-speaking-and-listening-practice-test/#application",
                                "name": "Versant Speaking & Listening AI Practice Simulator",
                                "operatingSystem": "All",
                                "applicationCategory": "EducationalApplication",
                                "offers": {
                                    "@type": "Offer",
                                    "price": "0.00",
                                    "priceCurrency": "USD"
                                }
                            },
                            {
                                "@type": "FAQPage",
                                "@id": "https://versantpro.com/versant-speaking-and-listening-practice-test/#faq",
                                "mainEntity": faqs.map(item => ({
                                    "@type": "Question",
                                    "name": item.q,
                                    "acceptedAnswer": {
                                        "@type": "Answer",
                                        "text": item.a
                                    }
                                }))
                            }
                        ]
                    })}
                </script>
            </Helmet>

            <Header page="speakingHub" />

            {/* Hero Banner Section */}
            <section className="relative w-full pt-16 pb-24 px-6 overflow-hidden bg-gradient-to-br from-emerald-900 via-green-800 to-teal-900 text-white">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay pointer-events-none"></div>
                <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-emerald-500 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob"></div>
                <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-72 h-72 bg-teal-500 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob animation-delay-2000"></div>
                
                <div className="max-w-6xl mx-auto relative z-10 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-emerald-100 text-xs sm:text-sm font-semibold mb-6 border border-white/20 shadow-lg">
                        <span className="w-2.5 h-2.5 bg-emerald-400 rounded-full animate-pulse shadow-[0_0_10px_rgba(52,211,153,0.8)]"></span>
                        Updated for 2026 Versant Speaking & Listening Standards
                    </div>
                    
                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight mb-6 leading-tight drop-shadow-md">
                        Versant Speaking & Listening Practice <br className="hidden sm:block"/>
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 via-teal-200 to-amber-300">
                            20 Free AI-Graded Mock Exams
                        </span>
                    </h1>
                    
                    <p className="text-lg sm:text-xl text-emerald-100 max-w-3xl mx-auto mb-10 font-light leading-relaxed">
                        Sharpen your pronunciation, speech rate, listening comprehension, and sentence rhythm. Real-time audio evaluation matching corporate score rubrics.
                    </p>

                    <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-xs sm:text-sm font-semibold text-emerald-200">
                        <span className="flex items-center gap-1.5"><FaCheckCircle className="text-emerald-400" /> 20 Full Length Speaking Mocks</span>
                        <span className="flex items-center gap-1.5"><FaCheckCircle className="text-emerald-400" /> Real-time Speech AI Engine</span>
                        <span className="flex items-center gap-1.5"><FaCheckCircle className="text-emerald-400" /> Instant Pronunciation & Fluency Breakdown</span>
                    </div>
                </div>
            </section>

            {/* Educational SEO Guide Section */}
            <section className="max-w-6xl mx-auto px-6 py-16 -mt-10 relative z-20">
                <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl p-6 sm:p-10 space-y-8">
                    
                    <div className="border-b border-slate-100 pb-6">
                        <div className="inline-flex items-center gap-2 text-emerald-600 font-extrabold text-xs uppercase tracking-widest bg-emerald-50 px-3 py-1 rounded-full mb-3">
                            <FaHeadphones /> Complete Assessment Overview
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                            Mastering the Versant English Speaking and Listening Test
                        </h2>
                        <p className="text-slate-600 text-sm sm:text-base leading-relaxed mt-3 font-normal">
                            The Versant English Speaking and Listening Test evaluates your ability to understand spoken English and express yourself fluently in professional settings. Used globally by Fortune 500 corporations, BPO contact centers, and IT firms to assess candidates for customer-facing and technical communication roles, this test relies entirely on automated speech analysis. Scoring focuses on four key pillars: <strong>Sentence Mastery, Vocabulary, Pronunciation, and Fluency</strong>.
                        </p>
                    </div>

                    {/* Section Breakdown Grid */}
                    <div className="grid md:grid-cols-3 gap-5">
                        {speakingSections.slice(0, 6).map((sec, idx) => (
                            <div key={sec.id} className="bg-slate-50 rounded-2xl p-5 border border-slate-100 hover:border-emerald-200 transition-colors">
                                <div className="flex items-center gap-3 font-bold text-slate-900 text-base mb-2">
                                    <span className="w-7 h-7 rounded-lg bg-emerald-500 text-white flex items-center justify-center text-xs font-black shrink-0">
                                        {idx + 1}
                                    </span>
                                    <span>{sec.name}</span>
                                </div>
                                <div className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider mb-2">{sec.count}</div>
                                <p className="text-xs text-slate-600 leading-relaxed font-light">
                                    {sec.description}
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* Pro Tip Box */}
                    <div className="bg-gradient-to-r from-slate-900 to-emerald-950 text-white rounded-2xl p-6 border border-emerald-500/30 flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="space-y-2">
                            <span className="inline-block px-3 py-1 bg-emerald-500/20 text-emerald-300 text-xs font-bold rounded-full border border-emerald-500/30 uppercase tracking-wider">
                                💡 Speech AI Tip
                            </span>
                            <h3 className="text-xl font-bold">Maintain Smooth & Unbroken Rhythm</h3>
                            <p className="text-xs sm:text-sm text-slate-300 max-w-2xl font-light leading-relaxed">
                                Avoid artificial pauses or self-corrections during Repeat Sentences and Story Retelling. The AI penalizes unnatural hesitation more heavily than minor word omissions.
                            </p>
                        </div>
                        <a 
                          href="#speaking-tests-grid" 
                          className="px-6 py-3 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold text-xs rounded-xl shadow-lg transition-transform active:scale-95 whitespace-nowrap shrink-0"
                        >
                            Try Free Demo Test ➔
                        </a>
                    </div>

                </div>
            </section>

            {/* 20 Mock Tests Grid Section */}
            <main id="speaking-tests-grid" className="max-w-6xl mx-auto px-6 pb-16">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                    <div>
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight flex items-center gap-3">
                            <span className="w-10 h-10 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center text-lg">
                                🎙️
                            </span>
                            Versant Speaking & Listening Mock Exams (1-20)
                        </h2>
                        <p className="text-slate-500 text-sm mt-1">Select a test below to start practicing with full AI speech evaluation.</p>
                    </div>
                    
                    {!isPremium && (
                        <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs font-semibold px-4 py-2 rounded-xl flex items-center gap-2 self-start sm:self-auto">
                            <span>⭐ Test 1 is 100% Free. Upgrade to unlock Tests 2–20.</span>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
                    {tests.map((test) => {
                        const isLocked = !isPremium && test.id > 1;

                        return (
                            <div
                                key={test.id}
                                className={`relative group bg-white rounded-3xl p-6 border-2 transition-all duration-300 flex flex-col justify-between ${
                                    isLocked
                                      ? 'border-slate-200/80 bg-slate-50/50 opacity-80 hover:opacity-100 hover:border-slate-300'
                                      : 'border-emerald-100 hover:border-emerald-500 shadow-md hover:shadow-2xl hover:-translate-y-1'
                                }`}
                            >
                                <div>
                                    <div className="flex justify-between items-start mb-4">
                                        <div className={`p-3 rounded-2xl ${isLocked ? 'bg-slate-200 text-slate-500' : 'bg-emerald-50 text-emerald-600'}`}>
                                            <FaMicrophone className="text-xl" />
                                        </div>
                                        
                                        <div className="flex items-center gap-2">
                                            {test.id === 1 && !isPremium && (
                                                <span className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[10px] font-black uppercase px-2.5 py-1 rounded-full shadow-md animate-pulse">
                                                    FREE DEMO
                                                </span>
                                            )}
                                            
                                            <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                                test.difficulty === 'Easy' ? 'bg-green-100 text-green-700' :
                                                test.difficulty === 'Medium' ? 'bg-amber-100 text-amber-700' :
                                                'bg-rose-100 text-rose-700'
                                            }`}>
                                                {test.difficulty}
                                            </span>
                                        </div>
                                    </div>

                                    <h3 className="text-xl font-extrabold text-slate-900 mb-2 group-hover:text-emerald-600 transition-colors">
                                        {test.title}
                                    </h3>
                                    <p className="text-xs sm:text-sm text-slate-500 leading-relaxed mb-6 font-light">
                                        {test.desc}
                                    </p>
                                </div>

                                <div>
                                    <div className="flex items-center gap-4 text-xs font-semibold text-slate-500 mb-5 border-t border-slate-100 pt-4">
                                        <span className="flex items-center gap-1.5"><FaClock className="text-slate-400" /> {test.duration}</span>
                                        <span className="flex items-center gap-1.5"><FaFileAlt className="text-slate-400" /> {test.questions} Qs</span>
                                    </div>

                                    <button
                                        onClick={() => handleStartTest(test.id)}
                                        className={`w-full py-3.5 rounded-2xl font-extrabold text-xs sm:text-sm flex items-center justify-center gap-2 transition-all shadow-md active:scale-95 ${
                                            isLocked
                                              ? 'bg-slate-200 text-slate-600 hover:bg-emerald-600 hover:text-white'
                                              : 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-500 hover:to-teal-500 shadow-emerald-500/20'
                                        }`}
                                    >
                                        {isLocked ? (
                                            <>Unlock Test {test.id} <FaLock className="text-xs" /></>
                                        ) : (
                                            <>Start Speaking Test <FaUnlock className="text-xs" /></>
                                        )}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Section Practice Module */}
                <div className="pt-8 border-t border-slate-200/80">
                    <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mb-2 flex items-center gap-3">
                        <span className="w-10 h-10 rounded-2xl bg-teal-100 text-teal-700 flex items-center justify-center text-lg">
                            🧩
                        </span>
                        Targeted Section Practice
                    </h2>
                    <p className="text-slate-500 text-sm mb-8 font-light">Focus on your specific weak spots with dedicated single-task drills.</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {speakingSections.map((section) => (
                            <div
                                key={section.id}
                                onClick={() => navigate('/versant-practice-hub', { state: { activeTab: 'speaking', selectedSection: section.id } })}
                                className="group relative bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm hover:shadow-xl hover:border-teal-400 transition-all duration-300 overflow-hidden cursor-pointer flex flex-col justify-between"
                            >
                                <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-emerald-500 to-teal-600 group-hover:w-2.5 transition-all"></div>

                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="w-12 h-12 rounded-2xl bg-teal-50 text-teal-600 flex items-center justify-center text-xl group-hover:scale-110 transition-transform">
                                            {section.icon}
                                        </div>
                                        <span className="text-[10px] font-black uppercase tracking-wider bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">
                                            {section.count}
                                        </span>
                                    </div>
                                    
                                    <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-teal-600 transition-colors">
                                        {section.name}
                                    </h3>
                                    <p className="text-xs sm:text-sm text-slate-500 leading-relaxed font-light mb-6">
                                        {section.description}
                                    </p>
                                </div>

                                <span className="text-xs font-bold text-teal-600 uppercase tracking-wider flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                                    Start Practice Drill <FaPlay className="text-[10px]" />
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </main>

            {/* FAQ Accordion Section */}
            <section className="bg-white border-t border-slate-200/80 py-16 px-6">
                <div className="max-w-4xl mx-auto space-y-8">
                    <div className="text-center space-y-3">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-100 text-emerald-800 text-xs font-bold rounded-full uppercase tracking-wider">
                            <FaQuestionCircle /> Speaking FAQ
                        </span>
                        <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
                            Frequently Asked Questions
                        </h2>
                        <p className="text-slate-500 text-sm sm:text-base font-light">
                            Everything you need to know about the Versant Speaking and Listening Test format and scoring.
                        </p>
                    </div>

                    <div className="space-y-4">
                        {faqs.map((faq, index) => (
                            <div 
                              key={index} 
                              className="border border-slate-200 rounded-2xl overflow-hidden transition-all duration-200 hover:border-emerald-300"
                            >
                                <button
                                  onClick={() => toggleFaq(index)}
                                  className="w-full p-5 sm:p-6 text-left font-bold text-slate-900 text-base sm:text-lg flex justify-between items-center gap-4 bg-slate-50/50 hover:bg-slate-50 transition-colors"
                                >
                                    <span>{faq.q}</span>
                                    <span className="text-emerald-600 shrink-0 text-sm">
                                        {openFaq === index ? <FaChevronUp /> : <FaChevronDown />}
                                    </span>
                                </button>
                                {openFaq === index && (
                                    <div className="p-5 sm:p-6 bg-white text-slate-600 text-sm sm:text-base leading-relaxed font-light border-t border-slate-100">
                                        {faq.a}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default SpeakingHub;

